import { formatCurrency, getCurrencySymbol } from "./currency";
/**
 * ============================================================================
 * TICKET GENERATOR - MASTER PRINT LAYOUT (PrintLayout Maestro)
 * ============================================================================
 * 
 * Este es el ÚNICO punto de generación de tickets para toda la aplicación.
 * TODAS las impresiones deben pasar por este módulo para garantizar:
 * - Consistencia visual al 100%
 * - Mismo logo, fuentes, márgenes en todos los tickets
 * - Un ticket de hace 3 meses debe verse IDÉNTICO a uno de hace 5 minutos
 * 
 * TIPOS SOPORTADOS:
 * - 'rental': Ticket de alquiler nuevo
 * - 'return': Ticket de devolución
 * - 'swap': Ticket de cambio/regularización
 * - 'movement': Ticket de movimiento de caja (venta, gasto, abono)
 * - 'closing': Cierre de caja
 */

// ============================================================================
// MASTER CSS - ESTILOS ÚNICOS PARA TODAS LAS IMPRESIONES
// ============================================================================
const getMasterCSS = (paperWidth = '80mm') => {
  // Layout parameters per paper size. A4 uses the same "receipt" layout as
  // 80mm but scaled up with generous side margins so it looks like a
  // proper printable receipt on A4 paper (not a stretched thin ticket).
  const isA4 = paperWidth === 'A4';
  const pageSize = isA4 ? 'A4' : paperWidth;
  const pageMargin = isA4 ? '15mm' : '0';
  const bodyWidth = isA4 ? '180mm' : paperWidth;      // 210mm − 2*15mm
  const bodyMaxWidth = bodyWidth;
  const fontStack = isA4
    ? "'Inter', system-ui, sans-serif"
    : "'Courier New', Consolas, Monaco, monospace";
  const fontSize = isA4 ? '13px' : '12px';
  return `
  /* ══════════════════════════════════════════════════════════════
     RESET GLOBAL
  ══════════════════════════════════════════════════════════════ */
  * { margin: 0; padding: 0; box-sizing: border-box; }

  /* ══════════════════════════════════════════════════════════════
     @page — Tamaño de papel. Debe estar FUERA de @media.
  ══════════════════════════════════════════════════════════════ */
  @page {
    size: ${pageSize} auto;
    margin: ${pageMargin} !important;
  }

  /* ══════════════════════════════════════════════════════════════
     HTML / BODY — Forzar ancho al tamaño del papel desde el inicio.
     Esto evita que el layout se calcule a ancho de ventana (400px)
     y luego tenga que corregirse solo en @media print.
  ══════════════════════════════════════════════════════════════ */
  html, body {
    width: ${bodyWidth};
    max-width: ${bodyMaxWidth};
    margin: 0 !important;
    padding: 0 !important;
    overflow-x: hidden;
    background: #fff;
    color: #000;
    font-family: ${fontStack};
    font-size: ${fontSize};
    line-height: 1.4;
  }

  /* ══════════════════════════════════════════════════════════════
     @media print — Reglas de impresión estrictas
  ══════════════════════════════════════════════════════════════ */
  @media print {
    @page {
      size: ${pageSize} auto;
      margin: ${pageMargin} !important;
    }

    html, body {
      width: ${bodyWidth} !important;
      max-width: ${bodyMaxWidth} !important;
      overflow: hidden !important;
      height: auto !important;
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    /* ── Ocultar SOLO el botón — NO usar visibility trick (causa bloques blancos) ── */
    .print-btn {
      display: none !important;
    }

    /* ── Ticket container ── */
    .thermal-ticket {
      position: static !important;
      display: block !important;
      width: 100% !important;
      max-width: ${bodyMaxWidth} !important;
      margin: 0 !important;
      padding: 4px !important;
      box-sizing: border-box !important;
      overflow: hidden !important;
    }

    /* ── DESTRUIR FLEX HORIZONTAL ──────────────────────────────────
       Los flex con justify-content: space-between se convierten en
       tablas para impresión térmica. Así la etiqueta queda a la
       izquierda y el valor a la derecha sobre el ancho real del papel.
    ─────────────────────────────────────────────────────────────── */
    .thermal-ticket .info-row,
    .thermal-ticket .row,
    .thermal-ticket .total-row {
      display: table !important;
      width: 100% !important;
      border-collapse: collapse !important;
      margin-bottom: 3px !important;
    }

    .thermal-ticket .info-row .label,
    .thermal-ticket .row > span:first-child {
      display: table-cell !important;
      width: 58% !important;
      text-align: left !important;
      vertical-align: top !important;
      word-break: break-word !important;
    }

    .thermal-ticket .info-row .value,
    .thermal-ticket .row .row-value {
      display: table-cell !important;
      width: 42% !important;
      text-align: right !important;
      vertical-align: top !important;
      font-weight: bold !important;
      word-break: break-word !important;
    }

    .thermal-ticket .total-row > span:first-child {
      display: table-cell !important;
      width: 55% !important;
      text-align: left !important;
      vertical-align: top !important;
    }

    .thermal-ticket .total-row > span:last-child {
      display: table-cell !important;
      width: 45% !important;
      text-align: right !important;
      vertical-align: top !important;
    }

    /* ── BLOQUES DE SECCIÓN: display: block ── */
    .thermal-ticket .section,
    .thermal-ticket .block,
    .thermal-ticket .total-section,
    .thermal-ticket .vat-breakdown {
      display: block !important;
      width: 100% !important;
    }

    /* ── LOGO CONTAINER — contención estricta ── */
    .thermal-ticket .logo-container {
      display: block !important;
      width: 100% !important;
      overflow: hidden !important;
      text-align: center !important;
    }

    /* ── SVG / IMG / CANVAS — evitar expansión al infinito ── */
    .thermal-ticket img,
    .thermal-ticket svg,
    .thermal-ticket canvas {
      display: block !important;
      max-width: 100% !important;
      width: auto !important;
      height: auto !important;
      object-fit: contain !important;
      overflow: hidden !important;
      margin: 0 auto !important;
    }

    /* ── TABLAS de artículos — sin bordes, precios a la derecha ── */
    .thermal-ticket table {
      width: 100% !important;
      border-collapse: collapse !important;
      border: none !important;
      table-layout: fixed !important;
    }
    .thermal-ticket table td,
    .thermal-ticket table th {
      border: none !important;
      padding: 1px 0 !important;
      overflow: hidden !important;
      word-break: break-word !important;
    }
    .thermal-ticket table td:last-child,
    .thermal-ticket table th:last-child {
      text-align: right !important;
    }

    /* ── HR y SEPARADORES — height explícito anti-expansión térmica ── */
    .thermal-ticket hr,
    .thermal-ticket .separator,
    .thermal-ticket .separator-double {
      display: block !important;
      height: 1px !important;
      max-height: 2px !important;
      border: none !important;
      border-top: 1px dashed #000 !important;
      margin: 4px 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
    }

    /* ── FONDOS GRISES → transparent en monocromo ── */
    .thermal-ticket .operation-number-box,
    .thermal-ticket .highlight-box,
    .thermal-ticket .date-range {
      background: transparent !important;
      border: 1px solid #000 !important;
    }
    .thermal-ticket .header-title-box,
    .thermal-ticket .amount-box,
    .thermal-ticket .result-box {
      background: transparent !important;
      border: 2px solid #000 !important;
    }

    /* ── TEXTO NEGRO PURO ── */
    .thermal-ticket, .thermal-ticket * {
      color: #000 !important;
    }

    /* ── SIN CORTES DE PÁGINA INTERNOS ── */
    .thermal-ticket .item-row,
    .thermal-ticket .info-row,
    .thermal-ticket .block,
    .thermal-ticket .section,
    .thermal-ticket .total-section,
    .thermal-ticket .total-row,
    .thermal-ticket tr,
    .thermal-ticket td,
    .thermal-ticket th,
    .thermal-ticket table,
    .thermal-ticket tbody {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
  }

  /* ══════════════════════════════════════════════════════════════
     ESTILOS BASE (pantalla) — Siguen funcionando a 80mm
  ══════════════════════════════════════════════════════════════ */
  .thermal-ticket {
    width: 100%;
    max-width: 100%;
    background: #fff;
    margin: 0;
    padding: 2px 4px;
    overflow: hidden;
    font-family: 'Courier New', Consolas, Monaco, monospace;
    font-size: 12px;
  }

  /* HEADER */
  .logo-container {
    text-align: center;
    margin-bottom: 4px;
    padding-bottom: 4px;
    border-bottom: 1px dashed #000;
    overflow: hidden;
  }
  .logo-container img {
    display: block;
    max-height: 50px;
    max-width: 100%;
    object-fit: contain;
    margin: 0 auto;
  }
  .company-name-fallback {
    font-size: 16px;
    font-weight: bold;
    text-align: center;
    padding: 4px 0;
    border-bottom: 1px dashed #000;
    margin-bottom: 4px;
  }

  .header-text {
    text-align: center;
    white-space: pre-wrap;
    margin-bottom: 4px;
    padding-bottom: 4px;
    border-bottom: 1px dashed #000;
    font-size: 10px;
    color: #000;
  }

  /* TÍTULO DEL TICKET */
  .ticket-title {
    text-align: center;
    font-weight: bold;
    font-size: 14px;
    margin: 10px 0;
    padding: 6px 0;
    border: 2px solid #000;
    color: #000;
    letter-spacing: 1px;
  }

  /* NÚMERO DE OPERACIÓN */
  .operation-number-box {
    font-family: monospace;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
    padding: 8px;
    background: #f0f0f0;
    border: 1px solid #000;
    margin: 8px 0;
  }

  /* FILAS DE INFORMACIÓN — flex solo en pantalla */
  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 4px;
    page-break-inside: avoid;
    color: #000;
  }
  .info-row .label { font-weight: normal; color: #000; flex: 0 0 58%; }
  .info-row .value { font-weight: bold; text-align: right; flex: 0 0 42%; color: #000; word-break: break-word; }

  /* SECCIONES */
  .section {
    margin: 10px 0;
    padding: 10px 0;
    border-top: 1px dashed #000;
    border-bottom: 1px dashed #000;
    page-break-inside: avoid;
  }
  .section-title, .block-title {
    font-weight: bold;
    font-size: 11px;
    text-transform: uppercase;
    margin-bottom: 6px;
    color: #000;
    letter-spacing: 0.5px;
  }

  /* BLOQUES */
  .block { margin: 10px 0; page-break-inside: avoid; }
  .row { display: flex; justify-content: space-between; align-items: flex-start; margin: 4px 0; color: #000; }
  .row-value { font-weight: bold; color: #000; text-align: right; }

  /* FILAS DE ARTÍCULOS */
  .item-row {
    display: flex;
    justify-content: space-between;
    margin: 4px 0;
    font-size: 10px;
    page-break-inside: avoid;
    color: #000;
  }
  .item-name { flex: 0 0 65%; overflow: hidden; text-overflow: ellipsis; color: #000; }
  .item-price { font-weight: bold; color: #000; text-align: right; }

  /* SECCIÓN TOTAL */
  .total-section {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #000;
    page-break-inside: avoid;
  }
  .total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: bold;
    font-size: 14px;
    color: #000;
  }

  /* CAJA DE IMPORTE DESTACADO */
  .amount-box {
    text-align: center;
    padding: 12px;
    margin: 10px 0;
    border: 2px solid #000;
  }
  .amount-label { font-size: 10px; color: #666; margin-bottom: 4px; }
  .amount-value { font-size: 20px; font-weight: bold; color: #000; }

  /* CAJA DE RESULTADO */
  .result-box {
    border: 2px solid #000;
    padding: 10px;
    margin: 10px 0;
    text-align: center;
  }
  .result-label { font-size: 10px; margin-bottom: 4px; color: #000; }
  .result-value { font-size: 16px; font-weight: bold; color: #000; }

  /* HIGHLIGHT BOX */
  .highlight-box {
    background: #f0f0f0;
    padding: 8px;
    margin: 8px 0;
    border: 1px solid #000;
  }

  /* NOTAS IVA */
  .vat-note { text-align: center; font-size: 9px; color: #000; margin-top: 4px; }
  .vat-breakdown { margin: 6px 0; font-size: 10px; color: #000; }

  /* RANGO DE FECHAS */
  .date-range {
    background: #f0f0f0;
    padding: 6px;
    border-radius: 4px;
    margin: 8px 0;
    text-align: center;
    font-size: 10px;
    color: #000;
    border: 1px solid #000;
    overflow: hidden;
  }
  .date-range strong { font-size: 11px; color: #000; }

  /* SEPARADORES */
  .separator { display: block; border: none; border-top: 1px dashed #000; margin: 8px 0; height: 1px; }
  .separator-double { display: block; border: none; border-top: 2px dashed #000; margin: 8px 0; height: 2px; }

  /* FOOTER */
  .footer-text {
    text-align: center;
    white-space: pre-wrap;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px dashed #000;
    font-size: 10px;
    color: #000;
  }

  /* TÉRMINOS LEGALES */
  .terms-text {
    text-align: center;
    font-size: 8px;
    color: #000;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px dashed #000;
  }

  /* BOTÓN IMPRIMIR */
  .print-btn {
    display: block;
    width: 100%;
    padding: 12px;
    margin-top: 15px;
    background: #2563eb;
    color: white;
    border: none;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    border-radius: 4px;
  }
  .print-btn:hover { background: #1d4ed8; }
`;
};

// ============================================================================
// UTILIDADES
// ============================================================================
function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

/**
 * Formats a date as short DD/MM for thermal ticket space efficiency
 */
function formatDateShort(dateStr) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
  } catch {
    return dateStr;
  }
}

/**
 * Adds N days to a date string and returns a new date string (YYYY-MM-DD)
 */
function addDaysToDate(dateStr, days) {
  if (!dateStr || !days) return dateStr;
  try {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + (days - 1)); // -1 because the start day counts
    return date.toISOString().split('T')[0];
  } catch {
    return dateStr;
  }
}

function formatTime(dateStr) {
  if (!dateStr) return '-';
  try {
    if (dateStr.includes('T')) {
      return dateStr.split('T')[1]?.substring(0, 5) || '-';
    }
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '-';
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  return `${formatDate(dateStr)} ${formatTime(dateStr)}`;
}

// ============================================================================
// TRADUCCIONES
// ============================================================================
const getTranslations = (language = 'es') => {
  const translations = {
    es: {
      print: 'IMPRIMIR',
      date: 'Fecha',
      time: 'Hora',
      customer: 'Cliente',
      dni: 'DNI/ID',
      items: 'Artículos',
      days: 'días',
      day: 'día',
      from: 'Desde',
      to: 'Hasta',
      dateRange: 'Periodo de alquiler',
      subtotal: 'Subtotal',
      vat: 'IVA',
      total: 'TOTAL',
      vatIncluded: 'IVA incluido',
      paymentMethod: 'Forma de pago',
      cash: 'Efectivo',
      card: 'Tarjeta',
      operationNumber: 'Nº Operación',
      rentalTicket: 'TICKET DE ALQUILER',
      returnTicket: 'TICKET DE DEVOLUCIÓN',
      swapTicket: 'TICKET DE CAMBIO',
      movementTicket: 'TICKET DE CAJA',
      closingTicket: 'CIERRE DE CAJA',
      saleTicket: 'TICKET DE VENTA',
      expenseTicket: 'TICKET DE SALIDA',
      refund: 'Devolución',
      supplement: 'Suplemento',
      adjustment: 'Ajuste',
      income: 'Ingresos',
      expense: 'Salidas',
      balance: 'Balance',
      returned: 'Devuelto',
      changed: 'Cambiado',
      category: 'Categoría',
      concept: 'Concepto',
      notes: 'Notas',
      amountCharged: 'IMPORTE COBRADO',
      amountRefunded: 'IMPORTE ABONADO',
      amountExpense: 'IMPORTE DE SALIDA',
      shift: 'Turno',
      printedAt: 'Impreso',
      responsible: 'Responsable',
      economicSummary: 'RESUMEN ECONÓMICO',
      openingBalance: 'Fondo Caja Inicial',
      sales: 'Ventas',
      refunds: 'Devoluciones',
      netIncome: 'INGRESO NETO',
      cashAudit: 'ARQUEO DE CAJA',
      expectedCash: 'Efectivo Esperado',
      countedCash: 'Efectivo Contado',
      expectedCard: 'Tarjeta Esperado',
      countedCard: 'Tarjeta Contado',
      discrepancy: 'Descuadre',
      statistics: 'ESTADÍSTICAS',
      operations: 'Operaciones',
      finalResult: 'RESULTADO FINAL',
      totalDiscrepancy: 'DESCUADRE TOTAL',
      deposit: 'Fianza',
      depositPrecharged: 'FIANZA COBRADA',
      depositReturned: 'Devuelta al Cliente',
      depositForfeited: 'Retenida por Daños',
      depositStatus: 'ESTADO DE LA FIANZA',
      pendingBalance: 'SALDO PENDIENTE A ABONAR EN DEVOLUCIÓN',
      mixedPaymentDetail: 'DESGLOSE DE PAGO'
    },
    en: {
      print: 'PRINT',
      date: 'Date',
      time: 'Time',
      customer: 'Customer',
      dni: 'ID',
      items: 'Items',
      days: 'days',
      day: 'day',
      from: 'From',
      to: 'Until',
      dateRange: 'Rental period',
      subtotal: 'Subtotal',
      vat: 'VAT',
      total: 'TOTAL',
      vatIncluded: 'VAT included',
      paymentMethod: 'Payment method',
      cash: 'Cash',
      card: 'Card',
      operationNumber: 'Operation #',
      rentalTicket: 'RENTAL TICKET',
      returnTicket: 'RETURN TICKET',
      swapTicket: 'EXCHANGE TICKET',
      movementTicket: 'CASH MOVEMENT',
      closingTicket: 'CASH CLOSING',
      saleTicket: 'SALE TICKET',
      expenseTicket: 'EXPENSE TICKET',
      refund: 'Refund',
      supplement: 'Supplement',
      adjustment: 'Adjustment',
      income: 'Income',
      expense: 'Expense',
      balance: 'Balance',
      returned: 'Returned',
      changed: 'Changed',
      category: 'Category',
      concept: 'Concept',
      notes: 'Notes',
      amountCharged: 'AMOUNT CHARGED',
      amountRefunded: 'AMOUNT REFUNDED',
      amountExpense: 'EXPENSE AMOUNT',
      shift: 'Shift',
      printedAt: 'Printed',
      responsible: 'Responsible',
      economicSummary: 'ECONOMIC SUMMARY',
      openingBalance: 'Opening Balance',
      sales: 'Sales',
      refunds: 'Refunds',
      netIncome: 'NET INCOME',
      cashAudit: 'CASH AUDIT',
      expectedCash: 'Expected Cash',
      countedCash: 'Counted Cash',
      expectedCard: 'Expected Card',
      countedCard: 'Counted Card',
      discrepancy: 'Discrepancy',
      statistics: 'STATISTICS',
      operations: 'Operations',
      finalResult: 'FINAL RESULT',
      totalDiscrepancy: 'TOTAL DISCREPANCY',
      deposit: 'Deposit',
      depositPrecharged: 'DEPOSIT CHARGED',
      depositReturned: 'Returned to Customer',
      depositForfeited: 'Retained for Damages',
      depositStatus: 'DEPOSIT STATUS',
      pendingBalance: 'PENDING BALANCE DUE AT RETURN',
      mixedPaymentDetail: 'PAYMENT BREAKDOWN'
    }
  };
  return translations[language] || translations.es;
};

// ============================================================================
// OBTENER CONFIGURACIÓN DESDE LOCALSTORAGE
// ============================================================================
export function getStoredSettings() {
  try {
    // Fresh-install defaults: hardware toggles come ON so the app works
    // out of the box. localStorage `null` means "never touched" → true.
    const rawAutoPrintOnPayment = localStorage.getItem('auto_print_on_payment');
    return {
      companyLogo: localStorage.getItem('company_logo') || localStorage.getItem('companyLogo') || null,
      ticketHeader: localStorage.getItem('ticket_header') || localStorage.getItem('ticketHeader') || 'TIENDA DE ALQUILER DE ESQUÍ',
      ticketFooter: localStorage.getItem('ticket_footer') || localStorage.getItem('ticketFooter') || '¡Gracias por su visita!',
      ticketTerms: localStorage.getItem('ticket_terms') || localStorage.getItem('ticketTerms') || '',
      showDniOnTicket: localStorage.getItem('show_dni_on_ticket') !== 'false',
      showVatOnTicket: localStorage.getItem('show_vat_on_ticket') === 'true',
      defaultVat: parseFloat(localStorage.getItem('default_vat')) || 21,
      vatIncludedInPrices: localStorage.getItem('vat_included_in_prices') !== 'false',
      language: localStorage.getItem('language') || 'es',
      paperWidth: localStorage.getItem('paper_width') || '80mm',
      // Hardware print mode: "browser" (window.print, universal) or
      // "escpos" (thermal via QZ Tray). Defaults to browser so the app
      // works on any hardware out of the box.
      printMode: localStorage.getItem('print_mode') || 'browser',
      qzPrinterName: localStorage.getItem('qz_printer_name') || null,
      autoPrintOnPayment: rawAutoPrintOnPayment === null ? true : rawAutoPrintOnPayment === 'true',
      printDoubleCopy: localStorage.getItem('print_double_copy') === 'true'
    };
  } catch {
    return {
      companyLogo: null,
      ticketHeader: 'TIENDA DE ALQUILER DE ESQUÍ',
      ticketFooter: '¡Gracias por su visita!',
      ticketTerms: '',
      showDniOnTicket: true,
      showVatOnTicket: false,
      defaultVat: 21,
      vatIncludedInPrices: true,
      language: 'es',
      paperWidth: '80mm',
      printMode: 'browser',
      qzPrinterName: null,
      autoPrintOnPayment: true,
      printDoubleCopy: false
    };
  }
}

// ============================================================================
// RENDER HEADER (Logo o Nombre de Empresa como Fallback)
// ============================================================================
function renderHeader(settings) {
  const { companyLogo, ticketHeader } = settings;
  
  // Si hay logo, mostrarlo
  if (companyLogo) {
    return `
      <div class="logo-container">
        <img src="${companyLogo}" alt="Logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
        <div class="company-name-fallback" style="display: none;">${escapeHtml(ticketHeader.split('\\n')[0] || 'MI EMPRESA')}</div>
      </div>
      ${ticketHeader ? `<div class="header-text">${escapeHtml(ticketHeader)}</div>` : ''}
    `;
  }
  
  // Fallback: Mostrar nombre de empresa en texto grande
  const companyName = ticketHeader ? ticketHeader.split('\n')[0] : 'MI EMPRESA';
  const restOfHeader = ticketHeader ? ticketHeader.split('\n').slice(1).join('\n') : '';
  
  return `
    <div class="company-name-fallback">${escapeHtml(companyName)}</div>
    ${restOfHeader ? `<div class="header-text">${escapeHtml(restOfHeader)}</div>` : ''}
  `;
}

// ============================================================================
// RENDER FOOTER
// ============================================================================
function renderFooter(settings) {
  const { ticketFooter, ticketTerms } = settings;
  let html = '';
  
  if (ticketFooter) {
    html += `<div class="footer-text">${escapeHtml(ticketFooter)}</div>`;
  }
  
  if (ticketTerms) {
    html += `<div class="terms-text">${escapeHtml(ticketTerms)}</div>`;
  }
  
  return html;
}

// ============================================================================
// CALCULAR IVA
// ============================================================================
function calculateVatInfo(total, vatRate, vatIncluded) {
  if (vatIncluded) {
    const base = total / (1 + vatRate / 100);
    const vat = total - base;
    return { base, vat, total };
  } else {
    const vat = total * (vatRate / 100);
    return { base: total, vat, total: total + vat };
  }
}

// ============================================================================
// RENDER BODY - SEGÚN TIPO DE TICKET
// ============================================================================

// RENTAL - Ticket de Alquiler
function renderRentalBody(data, settings, t) {
  const { showDniOnTicket, showVatOnTicket, defaultVat, vatIncludedInPrices } = settings;
  const vatInfo = calculateVatInfo(data.total || 0, defaultVat, vatIncludedInPrices);
  
  const paymentMethodLabel = 
    (data.paymentMethod === 'efectivo' || data.paymentMethod === 'cash') ? t.cash
    : (data.paymentMethod === 'mixed' || data.paymentMethod === 'mixto') ? `Mixto (${t.cash}+${t.card})`
    : t.card;

  // Calcular días si no viene directamente
  let rentalDays = data.days;
  if (!rentalDays && data.startDate && data.endDate) {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    rentalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }
  rentalDays = rentalDays || 1;

  // Renderizar items con 3 columnas: ARTÍCULO | DÍAS | IMPORTE
  // Con desglose de fechas por artículo + extensiones si las hay
  const renderItems = () => {
    const items = data.items || [];
    if (items.length === 0) {
      return '<div style="color: #666; font-style: italic; text-align: center; padding: 10px;">Sin artículos detallados</div>';
    }
    
    const isPack = (item) => item.item_type === 'pack' || item.isPack;

    return `
      <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
        <thead>
          <tr style="border-bottom: 1px solid #000;">
            <th style="text-align: left; padding: 3px 0; font-weight: bold;">ARTÍCULO</th>
            <th style="text-align: center; padding: 3px 0; font-weight: bold; white-space: nowrap;">DÍAS</th>
            <th style="text-align: right; padding: 3px 0; font-weight: bold; white-space: nowrap;">${getCurrencySymbol()}</th>
          </tr>
        </thead>
        <tbody>
          ${items.map(item => {
            const itemDays  = item.days || rentalDays;
            const itemPrice = item.price || item.subtotal || 0;
            const isPackItem = isPack(item);

            // ── Calcular fechas por artículo ──────────────────────────────
            // Si el item tiene fechas propias, usarlas.
            // Si no, calcular desde startDate global + itemDays
            const itemStartDate = item.startDate || data.startDate || null;
            const itemEndDate   = item.endDate
              || (itemStartDate && itemDays ? addDaysToDate(itemStartDate, itemDays) : null);

            // Mostrar fechas solo si están disponibles
            const dateRangeLine = (itemStartDate && itemEndDate)
              ? `<br/><span style="font-size:9px; color:#000;">${formatDateShort(itemStartDate)} → ${formatDateShort(itemEndDate)}</span>`
              : '';

            // ── Nombre de la línea principal ──────────────────────────────
            let mainLabel = escapeHtml(item.name || item.item_type || 'Artículo');
            if (!isPackItem && item.size) {
              mainLabel += ` <span style="font-size:9px;">(T.${escapeHtml(item.size)})</span>`;
            }

            // ── Línea secundaria: Nº de código ───────────────────────────
            let subLine = '';
            if (isPackItem && item.internal_code) {
              subLine = `<br/><span style="font-size:9px; font-weight:normal;">Incl: ${escapeHtml(item.internal_code)}</span>`;
            } else if (!isPackItem && item.internal_code) {
              subLine = `<br/><span style="font-size:9px; font-weight:bold;">Nº: ${escapeHtml(item.internal_code)}</span>`;
            }

            // ── Desglose de extensión (si el item fue ampliado) ───────────
            // item.extensionBreakdown = [{ days, amount, label }]
            const extensionRows = (item.extensionBreakdown || []).map(ext => `
              <tr style="page-break-inside: avoid;">
                <td style="padding: 1px 0 1px 8px; vertical-align: top; font-size: 9px;">
                  + Ampliación ${ext.days || ''}d${ext.label ? ` (${escapeHtml(ext.label)})` : ''}
                </td>
                <td style="text-align: center; padding: 1px 0; vertical-align: top; white-space: nowrap; font-size: 9px;">${ext.days || ''}d</td>
                <td style="text-align: right; padding: 1px 0; vertical-align: top; white-space: nowrap; font-size: 9px;">${formatCurrency(Number(ext.amount || 0))}</td>
              </tr>
            `).join('');

            return `
              <tr style="border-bottom: ${extensionRows ? 'none' : '1px dashed #ccc'}; page-break-inside: avoid;">
                <td style="padding: 3px 0; vertical-align: top;">
                  <span style="font-weight: bold;">${mainLabel}</span>${subLine}${dateRangeLine}
                </td>
                <td style="text-align: center; padding: 3px 0; vertical-align: top; white-space: nowrap;">
                  ${itemDays}${itemDays === 1 ? t.day : t.days}
                </td>
                <td style="text-align: right; padding: 3px 0; vertical-align: top; white-space: nowrap; font-weight: bold;">
                  ${formatCurrency(itemPrice)}
                </td>
              </tr>
              ${extensionRows}
              ${extensionRows ? '<tr><td colspan="3" style="border-bottom: 1px dashed #ccc; padding: 0;"></td></tr>' : ''}
            `;
          }).join('')}
        </tbody>
      </table>
    `;
  };

  return `
    ${data.operationNumber ? `
      <div class="operation-number-box">Nº ${escapeHtml(data.operationNumber)}</div>
    ` : ''}
    
    <div class="info-row">
      <span class="label">${t.date}:</span>
      <span class="value">${formatDate(data.date)}</span>
    </div>
    
    <div class="info-row">
      <span class="label">${t.customer}:</span>
      <span class="value">${escapeHtml(data.customer || data.customerName || '-')}</span>
    </div>
    
    ${showDniOnTicket && data.dni ? `
      <div class="info-row">
        <span class="label">${t.dni}:</span>
        <span class="value">${escapeHtml(data.dni)}</span>
      </div>
    ` : ''}
    
    ${data.startDate && data.endDate ? `
      <div class="date-range">
        <strong>${t.dateRange}</strong><br/>
        ${t.from}: ${formatDate(data.startDate)} - ${t.to}: ${formatDate(data.endDate)}<br/>
        <strong>(${rentalDays} ${rentalDays === 1 ? t.day : t.days})</strong>
      </div>
    ` : ''}
    
    <div class="section">
      <div class="section-title">${t.items}:</div>
      ${renderItems()}
    </div>
    
    ${showVatOnTicket ? `
      <div class="vat-breakdown">
        <div class="info-row">
          <span class="label">${t.subtotal}:</span>
          <span class="value">${formatCurrency(vatInfo.base)}</span>
        </div>
        <div class="info-row">
          <span class="label">${t.vat} (${defaultVat}%):</span>
          <span class="value">${formatCurrency(vatInfo.vat)}</span>
        </div>
      </div>
    ` : ''}
    
    <div class="total-section">
      <div class="total-row">
        <span>${t.total}:</span>
        <span>${formatCurrency((data.total || 0))}</span>
      </div>
      ${!showVatOnTicket && vatIncludedInPrices ? `<div class="vat-note">(${t.vatIncluded})</div>` : ''}
    </div>
    
    <div class="info-row" style="margin-top: 10px;">
      <span class="label">${t.paymentMethod}:</span>
      <span class="value">${paymentMethodLabel}</span>
    </div>

    ${(data.paymentMethod === 'mixed' || data.paymentMethod === 'mixto') && data.paymentDetail && (data.paymentDetail.cash > 0 || data.paymentDetail.card > 0) ? `
      <div style="border-top: 1px dashed #000; margin-top: 8px; padding-top: 6px;">
        <div class="section-title">${t.mixedPaymentDetail}:</div>
        <div class="info-row">
          <span class="label">${t.cash}:</span>
          <span class="value" style="font-weight:bold;">${formatCurrency(Number(data.paymentDetail.cash || 0))}</span>
        </div>
        <div class="info-row">
          <span class="label">${t.card}:</span>
          <span class="value" style="font-weight:bold;">${formatCurrency(Number(data.paymentDetail.card || 0))}</span>
        </div>
      </div>
    ` : ''}

    ${Number(data.deposit) > 0 ? `
      <div style="border: 2px solid #000; padding: 6px 8px; margin: 10px 0; text-align: center;">
        <div style="font-size: 9px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px;">${t.depositPrecharged}</div>
        <div style="font-size: 18px; font-weight: bold; margin-top: 2px;">${formatCurrency(Number(data.deposit))}</div>
        <div style="font-size: 9px; color: #000;">(A devolver al retorno del material)</div>
      </div>
    ` : ''}

    ${Number(data.pendingAmount) > 0 ? `
      <div style="border: 3px solid #000; padding: 8px; margin: 10px 0; text-align: center;">
        <div style="font-size: 9px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px;">${t.pendingBalance}</div>
        <div style="font-size: 20px; font-weight: bold; margin-top: 4px;">${formatCurrency(Number(data.pendingAmount))}</div>
      </div>
    ` : ''}
  `;
}

// MOVEMENT - Ticket de Movimiento de Caja (Venta, Gasto, Devolución)
function renderMovementBody(data, settings, t) {
  const isRefund = data.movementType === 'refund';
  const isExpense = data.movementType === 'expense';
  const isIncome = data.movementType === 'income';
  
  const amountLabel = isRefund ? t.amountRefunded : isExpense ? t.amountExpense : t.amountCharged;
  const amountPrefix = isIncome ? '+' : '-';
  
  const paymentMethodLabel = 
    (data.paymentMethod === 'efectivo' || data.paymentMethod === 'cash') ? t.cash
    : (data.paymentMethod === 'mixed' || data.paymentMethod === 'mixto') ? `Mixto (${t.cash}+${t.card})`
    : t.card;

  return `
    ${data.operationNumber ? `
      <div class="operation-number-box">Nº ${escapeHtml(data.operationNumber)}</div>
    ` : ''}
    
    <hr class="separator" />
    
    <div class="block">
      <div class="block-title">A. ${t.date} y ${t.time}</div>
      <div class="row">
        <span>${t.date}:</span>
        <span class="row-value">${formatDate(data.date || data.createdAt)}</span>
      </div>
      <div class="row">
        <span>${t.time}:</span>
        <span class="row-value">${formatTime(data.date || data.createdAt)}</span>
      </div>
      <div class="row">
        <span>${t.category}:</span>
        <span class="row-value">${escapeHtml(data.categoryLabel || data.category || '-')}</span>
      </div>
      <div class="row">
        <span>${t.paymentMethod}:</span>
        <span class="row-value">${paymentMethodLabel}</span>
      </div>
      ${data.customerName ? `
        <div class="row">
          <span>${t.customer}:</span>
          <span class="row-value">${escapeHtml(data.customerName)}</span>
        </div>
      ` : ''}
    </div>
    
    <hr class="separator" />
    
    <div class="block">
      <div class="block-title">B. ${t.concept}</div>
      <div class="highlight-box">
        ${escapeHtml(data.concept || '-')}
      </div>
      ${data.notes ? `
        <div style="margin-top: 6px; font-size: 10px;">
          ${t.notes}: ${escapeHtml(data.notes)}
        </div>
      ` : ''}
    </div>
    
    <hr class="separator" />
    
    <div class="amount-box">
      <div class="amount-label">${amountLabel}</div>
      <div class="amount-value">${amountPrefix}${formatCurrency(Math.abs(data.amount || 0))}</div>
    </div>
  `;
}

// CLOSING - Cierre de Caja
function renderClosingBody(data, settings, t) {
  const fmt = (v) => (v || 0).toFixed(2);
  const printTime = new Date().toLocaleString('es-ES', { 
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });

  return `
    <div class="info-row">
      <span class="label">${t.date}:</span>
      <span class="value">${escapeHtml(data.date)}</span>
    </div>
    <div class="info-row">
      <span class="label">${t.shift}:</span>
      <span class="value">#${data.closureNumber || data.shiftNumber || 1}</span>
    </div>
    <div class="info-row">
      <span class="label">${t.printedAt}:</span>
      <span class="value">${printTime}</span>
    </div>
    ${data.closedBy ? `
      <div class="info-row">
        <span class="label">${t.responsible}:</span>
        <span class="value">${escapeHtml(data.closedBy)}</span>
      </div>
    ` : ''}
    
    <hr class="separator-double" />
    
    <!-- BLOQUE A: RESUMEN ECONÓMICO -->
    <div class="block">
      <div class="block-title">A. ${t.economicSummary}</div>
      <div class="row">
        <span>(+) ${t.openingBalance}:</span>
        <span class="row-value">${formatCurrency(data.openingBalance)}</span>
      </div>
      <div class="row">
        <span>(+) ${t.sales}:</span>
        <span class="row-value">${formatCurrency(data.totalIncome || data.ingresosBrutos)}</span>
      </div>
      <div class="row">
        <span>(-) ${t.refunds}:</span>
        <span class="row-value">${formatCurrency(data.totalRefunds || data.devoluciones)}</span>
      </div>
      <div class="row">
        <span>(-) ${t.expense}:</span>
        <span class="row-value">${formatCurrency(data.totalExpense || data.gastos)}</span>
      </div>
      <div class="highlight-box" style="margin-top: 8px;">
        <div class="row" style="font-weight: bold;">
          <span>= ${t.netIncome}:</span>
          <span class="row-value">${formatCurrency(data.netIncome || data.balanceNeto)}</span>
        </div>
      </div>
    </div>
    
    <hr class="separator" />
    
    <!-- BLOQUE B: ARQUEO DE CAJA -->
    <div class="block">
      <div class="block-title">B. ${t.cashAudit}</div>
      
      <div style="margin-bottom: 8px; padding: 6px; border-left: 3px solid #000000;">
        <div style="font-weight: bold; margin-bottom: 4px;">${t.cash}</div>
        <div class="row" style="font-size: 10px;">
          <span>${t.expectedCash}:</span>
          <span>${formatCurrency(data.expectedCash || data.efectivoEsperado)}</span>
        </div>
        <div class="row" style="font-size: 10px;">
          <span>${t.countedCash}:</span>
          <span>${formatCurrency(data.countedCash || data.physicalCash)}</span>
        </div>
        <div class="row" style="font-size: 10px; font-weight: bold;">
          <span>${t.discrepancy}:</span>
          <span>${formatCurrency(data.discrepancyCash)}</span>
        </div>
      </div>
      
      <div style="padding: 6px; border-left: 3px solid #000000;">
        <div style="font-weight: bold; margin-bottom: 4px;">${t.card}</div>
        <div class="row" style="font-size: 10px;">
          <span>${t.expectedCard}:</span>
          <span>${formatCurrency(data.expectedCard || data.tarjetaEsperada)}</span>
        </div>
        <div class="row" style="font-size: 10px;">
          <span>${t.countedCard}:</span>
          <span>${formatCurrency(data.countedCard || data.cardTotal)}</span>
        </div>
        <div class="row" style="font-size: 10px; font-weight: bold;">
          <span>${t.discrepancy}:</span>
          <span>${formatCurrency(data.discrepancyCard)}</span>
        </div>
      </div>
    </div>
    
    <hr class="separator" />
    
    <!-- BLOQUE C: ESTADÍSTICAS -->
    ${data.totalOperations !== undefined ? `
      <div class="block">
        <div class="block-title">C. ${t.statistics}</div>
        <div class="row">
          <span>${t.operations}:</span>
          <span class="row-value">${data.totalOperations || 0}</span>
        </div>
      </div>
      <hr class="separator" />
    ` : ''}
    
    <!-- RESULTADO FINAL -->
    <div class="result-box">
      <div class="result-label">${t.finalResult}: ${t.totalDiscrepancy}</div>
      <div class="result-value">${formatCurrency(data.discrepancyTotal)}</div>
    </div>
  `;
}

// RETURN - Ticket de Devolución
function renderReturnBody(data, settings, t) {
  const { showDniOnTicket } = settings;
  
  return `
    ${data.operationNumber ? `
      <div class="operation-number-box">Nº ${escapeHtml(data.operationNumber)}</div>
    ` : ''}
    
    <div class="info-row">
      <span class="label">${t.date}:</span>
      <span class="value">${formatDate(data.date)}</span>
    </div>
    
    <div class="info-row">
      <span class="label">${t.customer}:</span>
      <span class="value">${escapeHtml(data.customer)}</span>
    </div>
    
    ${showDniOnTicket && data.dni ? `
      <div class="info-row">
        <span class="label">${t.dni}:</span>
        <span class="value">${escapeHtml(data.dni)}</span>
      </div>
    ` : ''}
    
    <div class="section">
      <div class="section-title">${t.returned}:</div>
      <table style="width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 4px;">
        <thead>
          <tr style="border-bottom: 1px solid #000;">
            <th style="text-align: left; padding: 2px 0; font-weight: bold;">ARTÍCULO</th>
            <th style="text-align: center; padding: 2px 0; font-weight: bold; white-space: nowrap;">DÍAS</th>
            <th style="text-align: right; padding: 2px 0; font-weight: bold; white-space: nowrap;">${getCurrencySymbol()}</th>
          </tr>
        </thead>
        <tbody>
          ${(data.returnedItems || []).map(item => {
            const itemDays  = item.days || '-';
            const itemPrice = (item.price != null && item.price !== 0)
              ? `${formatCurrency(Number(item.price))}`
              : '-';
            const subLine = item.internal_code
              ? `<br/><span style="font-size:9px; font-weight:bold;">Nº: ${escapeHtml(item.internal_code)}</span>`
              : '';
            const sizeStr = item.size
              ? ` <span style="font-size:9px;">(T.${escapeHtml(item.size)})</span>`
              : '';
            // Per-item date range
            const dateRangeLine = (item.startDate && item.endDate)
              ? `<br/><span style="font-size:9px; color:#000;">${formatDateShort(item.startDate)} → ${formatDateShort(item.endDate)}</span>`
              : '';

            return `
              <tr style="border-bottom: 1px dashed #ccc; page-break-inside: avoid;">
                <td style="padding: 2px 0; vertical-align: top;">
                  <span style="font-weight: bold; text-transform: uppercase;">${escapeHtml(item.name || item.item_type || 'Artículo')}</span>${sizeStr}${subLine}${dateRangeLine}
                </td>
                <td style="text-align: center; padding: 2px 0; vertical-align: top; white-space: nowrap;">${itemDays}d</td>
                <td style="text-align: right; padding: 2px 0; vertical-align: top; white-space: nowrap; font-weight: bold;">${itemPrice}</td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
    
    <div class="total-section">
      <div class="total-row">
        <span>${(data.refundAmount || 0) >= 0 ? t.refund : t.supplement}:</span>
        <span>${formatCurrency(Math.abs(data.refundAmount || 0))}</span>
      </div>
    </div>

    ${Number(data.depositOriginal) > 0 ? `
      <div style="border-top: 1px dashed #000; margin-top: 10px; padding-top: 8px;">
        <div class="section-title">${t.depositStatus}:</div>
        <div class="info-row">
          <span class="label">Original:</span>
          <span class="value">${formatCurrency(Number(data.depositOriginal))}</span>
        </div>
        ${Number(data.depositRefund) > 0 ? `
          <div class="info-row">
            <span class="label">${t.depositReturned}:</span>
            <span class="value" style="font-weight:bold;">${formatCurrency(Number(data.depositRefund))}</span>
          </div>
        ` : ''}
        ${(Number(data.depositOriginal) - Number(data.depositRefund || 0)) > 0.01 ? `
          <div class="info-row">
            <span class="label">${t.depositForfeited}:</span>
            <span class="value" style="font-weight:bold;">${formatCurrency((Number(data.depositOriginal) - Number(data.depositRefund || 0)))}</span>
          </div>
        ` : ''}
      </div>
    ` : ''}

    ${data.balanceBreakdown ? `
      <div style="margin-top: 8px; font-size: 9px; color: #000; border-top: 1px dashed #000; padding-top: 6px;">
        <span style="font-weight: bold;">Detalle: </span>${escapeHtml(data.balanceBreakdown)}
      </div>
    ` : ''}
  `;
}

// SWAP - Ticket de Cambio/Regularización
function renderSwapBody(data, settings, t) {
  const { showDniOnTicket } = settings;
  const paymentMethodLabel = 
    (data.paymentMethod === 'efectivo' || data.paymentMethod === 'cash') ? t.cash
    : (data.paymentMethod === 'mixed' || data.paymentMethod === 'mixto') ? `Mixto (${t.cash}+${t.card})`
    : t.card;
  
  return `
    ${data.operationNumber ? `
      <div class="operation-number-box">Nº ${escapeHtml(data.operationNumber)}</div>
    ` : ''}
    
    <div class="info-row">
      <span class="label">${t.date}:</span>
      <span class="value">${formatDateTime(data.date)}</span>
    </div>
    
    <div class="info-row">
      <span class="label">${t.customer}:</span>
      <span class="value">${escapeHtml(data.customer)}</span>
    </div>
    
    ${showDniOnTicket && data.dni ? `
      <div class="info-row">
        <span class="label">${t.dni}:</span>
        <span class="value">${escapeHtml(data.dni)}</span>
      </div>
    ` : ''}
    
    ${data.contractId ? `
      <div class="info-row">
        <span class="label">Contrato:</span>
        <span class="value">#${escapeHtml(data.contractId.substring(0, 8))}</span>
      </div>
    ` : ''}
    
    ${(data.oldItems || []).length > 0 ? `
      <div class="section">
        <div class="section-title">(-) ${t.returned}:</div>
        ${data.oldItems.map(item => `
          <div class="item-row">
            <span class="item-name">${escapeHtml(item.name || item.item_type || 'Artículo')}</span>
          </div>
        `).join('')}
      </div>
    ` : ''}
    
    ${(data.newItems || []).length > 0 ? `
      <div class="section">
        <div class="section-title">(+) ${t.changed}:</div>
        ${data.newItems.map(item => `
          <div class="item-row">
            <span class="item-name">${escapeHtml(item.name || item.item_type || 'Artículo')}</span>
          </div>
        `).join('')}
      </div>
    ` : ''}
    
    ${data.dateAdjustment ? `
      <div class="section">
        <div class="section-title">${t.adjustment} de Fecha:</div>
        <div class="row">
          <span>Días originales:</span>
          <span class="row-value">${data.originalDays || '-'}</span>
        </div>
        <div class="row">
          <span>Días nuevos:</span>
          <span class="row-value">${data.newDays || '-'}</span>
        </div>
        <div class="row">
          <span>Diferencia:</span>
          <span class="row-value">${data.daysDelta > 0 ? '+' : ''}${data.daysDelta || 0} días</span>
        </div>
      </div>
    ` : ''}
    
    <div class="total-section">
      <div class="total-row">
        <span>${(data.difference || 0) >= 0 ? t.supplement : t.refund}:</span>
        <span>${formatCurrency(Math.abs(data.difference || 0))}</span>
      </div>
    </div>
    
    ${(data.difference || 0) !== 0 ? `
      <div class="info-row" style="margin-top: 10px;">
        <span class="label">${t.paymentMethod}:</span>
        <span class="value">${paymentMethodLabel}</span>
      </div>
    ` : ''}
  `;
}

// ============================================================================
// OBTENER TÍTULO DEL TICKET
// ============================================================================
function getTicketTitle(ticketType, data, t) {
  switch (ticketType) {
    case 'rental':
      return t.rentalTicket;
    case 'return':
      return t.returnTicket;
    case 'swap':
      return t.swapTicket;
    case 'movement':
      // Determinar tipo específico de movimiento
      if (data.movementType === 'refund') return t.returnTicket;
      if (data.movementType === 'expense') return t.expenseTicket;
      return t.saleTicket;
    case 'closing':
      return t.closingTicket;
    default:
      return t.movementTicket;
  }
}

// ============================================================================
// FUNCIÓN PRINCIPAL - GENERAR HTML DEL TICKET
// ============================================================================
export function generateTicketHTML(options) {
  const { ticketType, data, settings: providedSettings } = options;
  
  // Obtener configuración (proporcionada o desde localStorage)
  const settings = providedSettings || getStoredSettings();
  const { language, paperWidth } = settings;
  
  // Traducciones
  const t = getTranslations(language);
  
  // Título del ticket
  const ticketTitle = getTicketTitle(ticketType, data, t);
  
  // Renderizar cuerpo según tipo
  let bodyHTML = '';
  switch (ticketType) {
    case 'rental':
      bodyHTML = renderRentalBody(data, settings, t);
      break;
    case 'return':
      bodyHTML = renderReturnBody(data, settings, t);
      break;
    case 'swap':
      bodyHTML = renderSwapBody(data, settings, t);
      break;
    case 'movement':
      bodyHTML = renderMovementBody(data, settings, t);
      break;
    case 'closing':
      bodyHTML = renderClosingBody(data, settings, t);
      break;
    default:
      bodyHTML = renderMovementBody(data, settings, t);
  }
  
  // Construir HTML completo
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${ticketTitle}</title>
      <style>${getMasterCSS(paperWidth)}</style>
    </head>
    <body>
      <div class="thermal-ticket">
        ${renderHeader(settings)}
        <div class="ticket-title">${ticketTitle}</div>
        ${bodyHTML}
        ${renderFooter(settings)}
      </div>
      <button class="print-btn" onclick="window.print(); setTimeout(() => window.close(), 500);">${t.print}</button>
    </body>
    </html>
  `;
}

// ============================================================================
// FUNCIÓN PARA IMPRIMIR TICKET - SISTEMA AGNÓSTICO
// ============================================================================
// Usa los drivers del sistema operativo a través del navegador.
// NO requiere configuración de IP ni WebUSB.
// Funciona con cualquier impresora marcada como "Predeterminada" en el sistema.
// ============================================================================

export async function printTicket(options) {
  const settings = options.settings || getStoredSettings();

  // ── Thermal ESC/POS route via QZ Tray ─────────────────────────────────
  // When the operator picks "Térmica ESC/POS" in Settings we try QZ Tray
  // first. If QZ is not reachable we fall back to the browser route so
  // the counter never gets stuck.
  const printMode = settings.printMode || localStorage.getItem('print_mode') || 'browser';
  if (printMode === 'escpos' && options.ticketType === 'rental') {
    try {
      const { printTicketESCPOS } = await import('./escposService.js');
      const ok = await printTicketESCPOS({
        data: options.data,
        settings,
        printerName: settings.qzPrinterName || localStorage.getItem('qz_printer_name') || undefined,
        paperWidth: settings.paperWidth || '80mm',
      });
      if (ok) return true;
      // Fall through to browser path if QZ Tray was not reachable.
      console.warn('[Print] QZ Tray no disponible — usando ruta del navegador');
    } catch (err) {
      console.warn('[Print] Error en escpos, fallback navegador:', err?.message || err);
    }
  }

  const html = generateTicketHTML(options);

  // iOS / iPadOS Safari: iframe.contentWindow.print() prints the PARENT page
  // (a known WebKit quirk — captures the current app screen instead of the ticket).
  // Use in-document isolation via CSS @media print instead.
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes('Macintosh') && 'ontouchend' in document); // iPadOS 13+ reports as Mac
  if (isIOS) {
    return printViaDomIsolation(html, settings);
  }

  // Desktop browsers: hidden iframe — silent, no popup, works reliably.
  return printViaIframe(html, settings);
}

/**
 * iOS/iPadOS print path.
 * Injects the ticket body into the current document inside a special container
 * and uses a print-only stylesheet that hides the rest of the app. Calling
 * window.print() then previews ONLY the ticket — no screen capture of the app.
 */
function printViaDomIsolation(html, settings) {
  return new Promise((resolve) => {
    // Extract <body> content and any <style> blocks from the generated HTML
    const cleanHtml = html.replace(/<button[\s\S]*?class="print-btn"[\s\S]*?<\/button>/gi, '');
    const bodyMatch = cleanHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    const bodyContent = bodyMatch ? bodyMatch[1] : cleanHtml;
    const styleMatches = cleanHtml.match(/<style[\s\S]*?<\/style>/gi) || [];
    const stylesInline = styleMatches.join('\n');

    // ── Overlay container ───────────────────────────────────────────────────
    const container = document.createElement('div');
    container.id = 'skiflow-print-root';
    // Hidden on screen; only visible to the printer
    container.setAttribute('aria-hidden', 'true');
    container.innerHTML = stylesInline + '<div class="skiflow-print-content">' + bodyContent + '</div>';
    document.body.appendChild(container);

    // ── Print-only CSS that isolates the ticket ─────────────────────────────
    const isolationCss = document.createElement('style');
    isolationCss.id = 'skiflow-print-isolation';
    isolationCss.textContent = `
      /* Offscreen on the live app */
      #skiflow-print-root { position: absolute; left: -99999px; top: 0; }

      @media print {
        /* Hide EVERYTHING in the document by default… */
        body > *:not(#skiflow-print-root) { display: none !important; }

        /* …except our ticket container, which we display front & center. */
        #skiflow-print-root {
          position: static !important;
          left: auto !important;
          top: auto !important;
          display: block !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        @page {
          size: ${settings.paperWidth === 'A4' ? 'A4' : (settings.paperWidth || '80mm')} auto;
          margin: ${settings.paperWidth === 'A4' ? '15mm' : '0'};
        }

        html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
      }
    `;
    document.head.appendChild(isolationCss);

    // ── afterprint cleanup ──────────────────────────────────────────────────
    const cleanup = () => {
      window.removeEventListener('afterprint', onAfter);
      setTimeout(() => {
        if (container.parentNode) container.parentNode.removeChild(container);
        if (isolationCss.parentNode) isolationCss.parentNode.removeChild(isolationCss);
      }, 200);
      resolve(true);
    };
    const onAfter = () => cleanup();
    window.addEventListener('afterprint', onAfter);

    // Safety net — some iOS Safari versions never fire afterprint
    setTimeout(cleanup, 15000);

    // Small delay so images/fonts paint before the print dialog opens
    setTimeout(() => {
      try {
        window.focus();
        window.print();
      } catch (err) {
        console.error('[Print] window.print() failed, using popup:', err);
        cleanup();
        printViaPopup(html, settings);
      }
    }, 250);
  });
}

/**
 * Imprime usando un iframe oculto — método silencioso preferido.
 *
 * Usa Blob URL (más fiable que contentDocument.write) para garantizar
 * que el iframe cargue completamente antes de disparar print().
 *
 * Garantías:
 *  - hasPrinted guard → print() se llama UNA SOLA VEZ
 *  - Fallback a popup si el iframe falla o es bloqueado
 *  - Limpieza automática del iframe y la Blob URL
 */
function printViaIframe(html, settings) {
  return new Promise((resolve) => {
    // Eliminar botón manual del ticket — no necesario en modo auto-print
    const printHTML = html.replace(/<button[\s\S]*?class="print-btn"[\s\S]*?<\/button>/gi, '');

    // ── Blob URL — carga más fiable que contentDocument.write ──────────────
    // Blob URL garantiza que el navegador parsea todo el HTML antes de disparar onload.
    let blobUrl;
    try {
      const blob = new Blob([printHTML], { type: 'text/html; charset=utf-8' });
      blobUrl = URL.createObjectURL(blob);
    } catch (blobError) {
      // Si Blob falla (entorno muy restringido), caer directamente en popup
      console.warn('[Print] Blob URL no disponible, usando popup:', blobError);
      printViaPopup(html, settings);
      return resolve(true);
    }

    // ── Iframe: 1×1px (nunca 0×0 — algunos motores omiten el render) ───────
    const iframe = document.createElement('iframe');
    iframe.style.cssText = [
      'position:fixed',
      'left:-9999px',
      'top:-9999px',
      'width:1px',
      'height:1px',
      'border:none',
      'visibility:hidden',
      'pointer-events:none',
    ].join(';');
    iframe.id = 'skiflow-print-' + Date.now();

    // ── Limpieza ─────────────────────────────────────────────────────────────
    const cleanup = () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1500);
    };

    // ── hasPrinted guard ─────────────────────────────────────────────────────
    let hasPrinted = false;

    // Wait until every <img> inside the iframe is fully decoded so the logo
    // never prints as a blank rectangle. A 3s watchdog guarantees we never
    // hang forever if an image is broken or the network stalls.
    async function waitForImages(doc) {
      const imgs = Array.from(doc.images || []);
      if (imgs.length === 0) return;
      const perImage = imgs.map((img) => {
        // Already fully decoded → resolve now.
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        // Prefer decode() (modern browsers) — falls back to load event.
        if (typeof img.decode === 'function') {
          return img.decode().catch(() => new Promise((res) => {
            img.addEventListener('load', res, { once: true });
            img.addEventListener('error', res, { once: true });
          }));
        }
        return new Promise((res) => {
          img.addEventListener('load', res, { once: true });
          img.addEventListener('error', res, { once: true });
        });
      });
      const watchdog = new Promise((res) => setTimeout(res, 3000));
      await Promise.race([Promise.all(perImage), watchdog]);
    }

    // ── onload — se dispara cuando el documento del iframe está listo ────────
    iframe.onload = async () => {
      if (hasPrinted) return;           // guard: onload re-fired → ignorar
      hasPrinted = true;

      try {
        // Wait for every logo / image inside the ticket to be fully decoded
        // BEFORE calling print(). This is the real cause of "logo prints
        // as a blank box" and it is what the old fixed 500ms timeout
        // tried (and often failed) to hide.
        await waitForImages(iframe.contentDocument);
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        if (settings.printDoubleCopy) {
          setTimeout(() => {
            try { iframe.contentWindow.print(); } catch (_) { /* silent */ }
            cleanup();
            resolve(true);
          }, 2000);
        } else {
          cleanup();
          resolve(true);
        }
      } catch (printError) {
        console.error('[Print] Error en iframe.print(), usando popup:', printError);
        cleanup();
        printViaPopup(html, settings);
        resolve(true);
      }
    };

    // ── onerror — fallback inmediato si el iframe no puede cargar ───────────
    iframe.onerror = () => {
      console.warn('[Print] iframe.onerror → fallback popup');
      cleanup();
      printViaPopup(html, settings);
      resolve(true);
    };

    // ── Añadir al DOM y asignar src (Blob URL) ───────────────────────────────
    document.body.appendChild(iframe);
    iframe.src = blobUrl;
  });
}

/**
 * Imprime usando ventana popup — fallback si el iframe falla.
 * Auto-imprime usando tanto onload como readyState para mayor compatibilidad.
 */
function printViaPopup(html, settings) {
  const printWindow = window.open('', '_blank', 'width=340,height=800,scrollbars=no,menubar=no,toolbar=no,location=no,status=no');

  if (!printWindow) {
    console.error('[Print] Popup bloqueado. Permite popups para esta página.');
    alert('No se pudo imprimir: el navegador bloqueó la ventana emergente.\nVe a la configuración del navegador y permite popups para esta página.');
    return false;
  }

  // Inyectar script de auto-print compatible con onload Y readyState
  const autoScript = `
    <script>
      var _printed = false;
      function doPrint() {
        if (_printed) return;
        _printed = true;
        setTimeout(function() {
          window.print();
          setTimeout(function() { window.close(); }, 600);
        }, 200);
      }
      // Disparar en onload (carga completa)
      window.onload = doPrint;
      // Guardia adicional: si readyState ya es 'complete' cuando el script se ejecuta
      if (document.readyState === 'complete') doPrint();
    </script>`;

  const autoHTML = html
    .replace(/<button[\s\S]*?class="print-btn"[\s\S]*?<\/button>/gi, '')
    .replace('</head>', autoScript + '</head>');

  printWindow.document.open();
  printWindow.document.write(autoHTML);
  printWindow.document.close();
  printWindow.focus();

  if (settings.printDoubleCopy) {
    setTimeout(() => {
      const second = window.open('', '_blank', 'width=340,height=800');
      if (second) {
        second.document.open();
        second.document.write(autoHTML);
        second.document.close();
        second.focus();
      }
    }, 2500);
  }

  return true;
}

/**
 * Imprime directamente sin preguntar (para modo kiosco)
 * Requiere que el usuario haya configurado su navegador en modo kiosco
 */
export function printTicketSilent(options) {
  return printTicket(options);
}

/**
 * Imprime un ticket de PRUEBA con datos dummy, respetando el ancho de
 * papel actual. Sirve para validar la impresora en un click desde
 * Configuración > Hardware.
 */
export function printTestTicket(customSettings) {
  const settings = customSettings || getStoredSettings();
  const now = new Date();
  const dummy = {
    operation_number: 'TEST-' + now.getTime().toString().slice(-6),
    customer_name: 'Cliente de prueba',
    customer_dni: '00000000A',
    customer_phone: '000 000 000',
    items: [
      {
        item_type: 'Ski (test)',
        size: 'M',
        barcode: 'TEST-0001',
        days: 1,
        price: 25,
      },
    ],
    days: 1,
    total_amount: 25,
    paid_amount: 25,
    pending_amount: 0,
    payment_method: 'cash',
    start_date: now.toISOString(),
    end_date: now.toISOString(),
    created_at: now.toISOString(),
    deposit: 0,
    is_test: true,
  };
  return printTicket({
    ticketType: 'rental',
    data: dummy,
    settings,
  });
}

// ============================================================================
// EXPORTAR TODO
// ============================================================================
export default {
  generateTicketHTML,
  printTicket,
  printTicketSilent,
  printTestTicket,
  getStoredSettings
};
